import React, { useCallback, useEffect, useRef, useState } from "react";import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-client Google Search Console connection panel.
 * Mirrors ClientGoogleAds: OAuth → list properties → select verified property →
 * manage branded-keyword chips (auto-suggested + admin-editable).
 *
 * Props:
 *   - clientId
 *   - isSuperAdmin (write disabled when false)
 */
export default function ClientSearchConsole({ clientId, isSuperAdmin = true }) {
  const [status, setStatus] = useState(null);
  const [properties, setProperties] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // Branded keyword editor state
  const [branded, setBranded] = useState({ suggestions: [], selected: [] });
  const [customKw, setCustomKw] = useState("");
  const [savingBranded, setSavingBranded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/search-console/status`);
      setStatus(res.data);
    } catch (e) {
      setStatus(null);
      setErr(e.response?.data?.detail || e.message);
    }
  }, [clientId]);

  const loadBranded = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/search-console/branded-suggest`);
      setBranded(res.data);
    } catch {
      /* non-fatal */
    }
  }, [clientId]);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("gsc_connected") === "1") {
      setMsg("Search Console connected. Now pick the verified property to link.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("gsc_error")) {
      setErr(`OAuth error: ${params.get("gsc_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load]);

  useEffect(() => {
    if (status?.property_linked) loadBranded();
  }, [status?.property_linked, loadBranded]);

  const connect = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const res = await axios.get(`${API}/clients/${clientId}/search-console/auth-url`);
      window.location.href = res.data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const fetchProperties = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const res = await axios.get(`${API}/clients/${clientId}/search-console/properties`);
      setProperties(res.data.properties || []);
      if (!res.data.properties?.length) setMsg("No verified properties returned. Verify in Search Console first.");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const selectProperty = async (p) => {
    setBusy(true); setErr(""); setMsg("");
    try {
      await axios.put(`${API}/clients/${clientId}/search-console/select-property`, {
        site_url: p.site_url,
        permission_level: p.permission_level,
      });
      setMsg(`Linked: ${p.site_url}`);
      setProperties([]);
      await load();
      await loadBranded();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Search Console for this client?")) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await axios.post(`${API}/clients/${clientId}/search-console/disconnect`);
      setMsg("Disconnected.");
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const toggleBranded = (kw) => {
    setBranded(prev => {
      const has = prev.selected.includes(kw);
      return { ...prev, selected: has ? prev.selected.filter(k => k !== kw) : [...prev.selected, kw] };
    });
  };

  const addCustomBranded = () => {
    const k = customKw.trim().toLowerCase();
    if (!k) return;
    setBranded(prev => ({
      ...prev,
      selected: prev.selected.includes(k) ? prev.selected : [...prev.selected, k],
      suggestions: prev.suggestions.includes(k) ? prev.suggestions : [...prev.suggestions, k],
    }));
    setCustomKw("");
  };

  const removeBranded = (kw) => {
    setBranded(prev => ({
      ...prev,
      selected: prev.selected.filter(k => k !== kw),
    }));
  };

  // Debounced auto-save whenever the selected list changes (after initial load)
  const initialSelectedRef = useRef(null);
  useEffect(() => {
    if (!status?.property_linked) return;
    // Skip auto-save on first render (when we just loaded from server)
    if (initialSelectedRef.current === null) {
      initialSelectedRef.current = JSON.stringify(branded.selected);
      return;
    }
    const current = JSON.stringify(branded.selected);
    if (current === initialSelectedRef.current) return;
    // Debounce 400ms
    const t = setTimeout(async () => {
      setSavingBranded(true); setErr("");
      try {
        await axios.put(`${API}/clients/${clientId}/search-console/branded-keywords`, {
          branded_keywords: branded.selected,
        });
        initialSelectedRef.current = current;
      } catch (e) {
        setErr(e.response?.data?.detail || e.message || "Failed to save branded keywords");
      } finally {
        setSavingBranded(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [branded.selected, status?.property_linked, clientId]);

  if (!status) {
    return <div className="card-flat p-4 text-[11px] text-[#71717A]">Loading Search Console status…</div>;
  }

  const tokenMissing = !status.configured;
  const connected = status.connected;
  const linked = status.property_linked;

  return (
    <div className="card-flat p-5 space-y-3" data-testid="client-search-console-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Google Search Console <span className="text-[10px] text-[#71717A] font-normal">(SEO)</span></h3>
          <p className="text-[11px] text-[#71717A]">
            {tokenMissing
              ? "Integration not configured yet — super-admin must set GSC_CLIENT_ID + GSC_CLIENT_SECRET in backend/.env (or reuse Google Ads OAuth credentials)."
              : !connected
                ? "Connect this client's verified Search Console property to pull SEO performance + AI brief."
                : !linked
                  ? "Authenticated. Pick the verified property to link."
                  : `Linked: ${status.site_url}`}
          </p>
        </div>
        <span
          className="text-[10px] px-2 py-0.5 rounded font-medium uppercase tracking-wider"
          style={{
            background: linked ? "#10B98122" : connected ? "#F59E0B22" : tokenMissing ? "#52525B22" : "#FF444422",
            color: linked ? "#6EE7B7" : connected ? "#FCD34D" : tokenMissing ? "#A1A1AA" : "#FF8080",
          }}
          data-testid="gsc-status-pill"
        >
          {linked ? "Linked" : connected ? "Property pending" : tokenMissing ? "Setup pending" : "Not connected"}
        </span>
      </div>

      {!isSuperAdmin && (
        <p className="text-[10px] text-[#71717A] italic">Super-admin only — contact your agency to connect.</p>
      )}

      {isSuperAdmin && !tokenMissing && !connected && (
        <button type="button" onClick={connect} disabled={busy} className="btn-primary text-xs" data-testid="gsc-connect-btn">
          {busy ? "Redirecting…" : "Connect with Google"}
        </button>
      )}

      {isSuperAdmin && connected && !linked && (
        <div className="space-y-2">
          <button type="button" onClick={fetchProperties} disabled={busy} className="btn-secondary text-xs" data-testid="gsc-fetch-properties-btn">
            {busy ? "Fetching…" : "Fetch verified properties"}
          </button>
          {properties.length > 0 && (
            <div className="border border-[#2A2A2A] rounded max-h-60 overflow-auto" data-testid="gsc-properties-list">
              {properties.map((p) => (
                <button type="button" key={p.site_url}
                  onClick={() => selectProperty(p)}
                  disabled={busy}
                  className="w-full text-left px-3 py-2 border-b border-[#2A2A2A] last:border-b-0 hover:bg-[#1A1A1A] text-xs flex items-center justify-between"
                  data-testid={`gsc-property-${(p.site_url || "").replace(/[^a-z0-9]/gi, "_")}`}>
                  <span>
                    <span className="font-medium">{p.site_url}</span>
                    {p.is_domain_property && <span className="ml-2 text-[10px] text-[#FCD34D]">DOMAIN</span>}
                  </span>
                  <span className="text-[10px] text-[#A1A1AA]">{p.permission_level}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isSuperAdmin && linked && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <button type="button" onClick={disconnect} disabled={busy} className="text-[11px] px-2 py-1 rounded border border-[#FF444444] text-[#FF8080] hover:bg-[#FF444411]" data-testid="gsc-disconnect-btn">
              Disconnect
            </button>
            <span className="text-[10px] text-[#71717A]">{status.permission_level}</span>
          </div>

          {/* Branded keyword editor */}
          <div className="border-t border-[#27272A] pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-[12px] font-semibold text-white/90">Branded Keywords</h4>
                <p className="text-[10px] text-[#71717A]">These count as branded queries. Click ✕ to remove. Auto-saves.</p>
              </div>
              {savingBranded && <span className="text-[10px] text-white/50">Saving…</span>}
            </div>

            {/* Row 1 — SELECTED chips (each with clear × to remove) */}
            <div className="flex flex-wrap gap-1.5 min-h-[24px]" data-testid="gsc-branded-selected">
              {branded.selected.length === 0 && (
                <div className="text-[10px] text-white/40 italic py-1">
                  No branded terms selected. Add from suggestions below or type a custom term.
                </div>
              )}
              {branded.selected.map(kw => (
                <span key={kw} className="text-[10px] pl-2 pr-1 py-0.5 rounded border bg-[#FFEB00] text-black border-[#FFEB00] font-semibold inline-flex items-center gap-1" data-testid={`gsc-branded-selected-${kw}`}>
                  {kw}
                  <button
                    type="button"
                    onClick={() => removeBranded(kw)}
                    className="w-4 h-4 rounded hover:bg-black/20 flex items-center justify-center leading-none text-black/70 hover:text-black text-[11px]"
                    aria-label={`Remove ${kw}`}
                    data-testid={`gsc-branded-remove-${kw}`}
                    title="Remove"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>

            {/* Row 2 — SUGGESTIONS not yet selected (click to add) */}
            {branded.suggestions.filter(k => !branded.selected.includes(k)).length > 0 && (
              <div className="pt-1">
                <div className="text-[9px] uppercase tracking-wider text-white/40 mb-1">Suggestions · click to add</div>
                <div className="flex flex-wrap gap-1.5" data-testid="gsc-branded-suggestions-row">
                  {branded.suggestions.filter(k => !branded.selected.includes(k)).map(kw => (
                    <button
                      type="button"
                      key={kw}
                      onClick={() => toggleBranded(kw)}
                      className="text-[10px] px-2 py-0.5 rounded border bg-white/[0.04] text-white/60 border-white/15 border-dashed hover:border-white/40 hover:text-white/85 transition-colors"
                      data-testid={`gsc-branded-suggest-${kw}`}
                    >
                      + {kw}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Row 3 — custom input */}
            <div className="flex items-center gap-2 pt-1">
              <input
                value={customKw}
                onChange={e => setCustomKw(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCustomBranded(); } }}
                placeholder="Add custom branded term (press Enter)…"
                className="flex-1 text-[11px] px-2 py-1 rounded bg-[#0A0A0A] border border-[#2A2A2A] text-white placeholder:text-white/30 focus:outline-none focus:border-[#FFEB00]/50"
                data-testid="gsc-branded-custom-input"
              />
              <button type="button" onClick={addCustomBranded} className="text-[10px] px-2 py-1 rounded border border-white/15 text-white/70 hover:bg-white/[0.04]" data-testid="gsc-branded-add-btn">
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {msg && <p className="text-[11px] text-[#6EE7B7]" data-testid="gsc-msg">{msg}</p>}
      {err && <p className="text-[11px] text-[#FF8080]" data-testid="gsc-err">{err}</p>}
    </div>
  );
}
